import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { config } from "../config";

type Recording = {
  prefix: string;
  startedAt: Date;
  archiveUrl: string;
  downloadUrl?: string;
  fallbackUrl: string;
  transcriptUrl?: string;
  thumbUrl?: string;
};

export default function ArchivePage() {
  const [items, setItems] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<Recording | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const feedUrl = config.archiveFeedUrl || `${config.gatewayUrl.replace(/\/$/, "")}/archive/index.json`;
        const res = await fetch(feedUrl, { cache: "no-store" });
        if (!res.ok) throw new Error(`Archive feed failed (${res.status})`);
        const data = await res.json() as { items?: Array<{ prefix?: string; startedAt?: string; archiveUrl?: string; downloadUrl?: string; fallbackUrl?: string; transcriptUrl?: string; thumbUrl?: string; src?: string; fallbackSrc?: string }> };
        const recordings: Recording[] = (data.items ?? [])
          .map((item) => parseRecording(item))
          .filter((r): r is Recording => r !== null)
          .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
        setItems(recordings);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "Failed to load archive");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="viewer">
      <h1>Archive</h1>
      {loading && <p className="muted">Loading recordings…</p>}
      {err && <p className="err">{err}</p>}
      {!loading && items.length === 0 && <p className="muted">No recordings yet.</p>}

      <div className="archive-grid">
        {items.map((r) => (
          <button key={r.prefix} className="archive-card" onClick={() => setActive(r)}>
            {r.thumbUrl ? (
              <img className="archive-card__thumb" src={r.thumbUrl} alt="" loading="lazy" />
            ) : (
              <div className="archive-card__thumb archive-card__thumb--placeholder" />
            )}
            <div>
              <div className="archive-card__when">{r.startedAt.toLocaleString()}</div>
              <div className="muted small">{r.prefix}</div>
            </div>
          </button>
        ))}
      </div>

      {active && <PlayerModal recording={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function PlayerModal({ recording, onClose }: { recording: Recording; onClose: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    let hls: Hls | null = null;
    let triedFallback = false;
    const play = (src: string) => {
      if (src.endsWith(".m3u8") && Hls.isSupported() && !v.canPlayType("application/vnd.apple.mpegurl")) {
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(v);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          void v.play().catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal && !triedFallback) {
            triedFallback = true;
            hls?.destroy();
            play(recording.fallbackUrl);
          }
        });
        return;
      }

      v.src = src;
      void v.play().catch(() => {
        if (!triedFallback && src !== recording.fallbackUrl) {
          triedFallback = true;
          play(recording.fallbackUrl);
        }
      });
    };

    play(recording.archiveUrl);
    return () => hls?.destroy();
  }, [recording.archiveUrl, recording.fallbackUrl]);

  return (
    <div className="modal" role="dialog" onClick={onClose}>
      <div className="modal__body" onClick={(e) => e.stopPropagation()}>
        <button className="modal__close" onClick={onClose}>×</button>
        <video ref={ref} controls autoPlay playsInline />
        <p className="muted">{recording.startedAt.toLocaleString()}</p>
        {(recording.downloadUrl || recording.archiveUrl) && (
          <p className="row">
            <a
              className="btn ghost"
              href={recording.downloadUrl || recording.archiveUrl}
              download
              target="_blank"
              rel="noopener"
            >
              Download video
            </a>
          </p>
        )}
        {recording.transcriptUrl && (
          <p>
            <a className="btn ghost" href={recording.transcriptUrl} target="_blank" rel="noopener">
              Download transcript
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

function parseRecording(item: { prefix?: string; startedAt?: string; archiveUrl?: string; downloadUrl?: string; fallbackUrl?: string; transcriptUrl?: string; thumbUrl?: string; src?: string; fallbackSrc?: string }): Recording | null {
  const prefix = String(item.prefix ?? "").trim();
  const startedAt = item.startedAt ? new Date(item.startedAt) : null;
  const archiveUrl = String(item.archiveUrl ?? item.src ?? "").trim();
  const fallbackUrl = String(item.fallbackUrl ?? item.fallbackSrc ?? "").trim();
  if (!prefix || !startedAt || Number.isNaN(startedAt.getTime()) || !archiveUrl || !fallbackUrl) return null;
  return {
    prefix,
    startedAt,
    archiveUrl,
    downloadUrl: String(item.downloadUrl ?? "").trim() || undefined,
    fallbackUrl,
    transcriptUrl: String(item.transcriptUrl ?? "").trim() || undefined,
    thumbUrl: String(item.thumbUrl ?? "").trim() || undefined,
  };
}
